import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Table,
  ProgressBar,
} from "react-bootstrap";
// import "./styles.module.css";
import { Box } from "./brokenLinkStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";

interface CheckResult {
  status: string;
  url: string;
  anchorText: string;
  linkedFrom: string;
  isBroken?: boolean;
}

const BrokenLinkChecker: React.FC = () => {
  const [url, setUrl] = useState<string>("");
  const [checkMode, setCheckMode] = useState<"single" | "whole">("single");
  const [allResults, setAllResults] = useState<CheckResult[]>([]);
  const [displayedResults, setDisplayedResults] = useState<CheckResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [totalUrls, setTotalUrls] = useState<number>(0);
  const [checkedUrls, setCheckedUrls] = useState<number>(0);
  const [okCount, setOkCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);
  const [showFullReport, setShowFullReport] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isChecking || !url) return;

    setIsChecking(true);
    setIsLoading(true);
    setError("");
    setAllResults([]);
    setDisplayedResults([]);
    setCheckedUrls(0);
    setTotalUrls(0);
    setOkCount(0);
    setFailedCount(0);
    setProgress(0);
    setShowFullReport(false);

    try {
      if (checkMode === "single") {
        const baseUrl = process.env.REACT_APP_BASE_API;
        const apiUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (!response.ok || !data.success || !Array.isArray(data.data)) {
          throw new Error("Failed to fetch or parse links.");
        }

        const linkResults: CheckResult[] = data.data.map((item: any) => ({
          url: item.link,
          status: item.status,
          isBroken: item.isBroken,
          anchorText: "link:href",
          linkedFrom: url,
        }));

        setAllResults(linkResults);
        setDisplayedResults(linkResults.filter((link) => link.isBroken));

        setTotalUrls(linkResults.length);
        setCheckedUrls(linkResults.length);
        setOkCount(linkResults.filter((l) => !l.isBroken).length);
        setFailedCount(linkResults.filter((l) => l.isBroken).length);
        setProgress(100);
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      } else {
        const baseUrl = process.env.REACT_APP_BASE_API;
        const apiUrl = `${baseUrl}/api/site-map/allLink?url=${encodeURIComponent(
          url
        )}`;
        const response = await fetch(apiUrl);
        const data2 = await response.json();

        if (data2.data && Array.isArray(data2.data)) {
          const totalLinks = data2.data.length;
          let runningResults: CheckResult[] = [];

          for (let i = 0; i < totalLinks; i++) {
            const pageUrl = data2.data[i];
            const eUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(
              pageUrl
            )}`;
            const res = await fetch(eUrl);
            const resData = await res.json();

            if (!res.ok || !resData.success || !Array.isArray(resData.data)) {
              console.warn(`Failed to fetch links for ${pageUrl}`);
              continue;
            }

            const linkResults: CheckResult[] = resData.data.map(
              (item: any) => ({
                url: item.link,
                status: item.status,
                isBroken: item.isBroken,
                anchorText: "link:href",
                linkedFrom: url,
              })
            );

            runningResults = [...runningResults, ...linkResults];

            // Deduplicate by URL
            const uniqueResults = Array.from(
              new Map(runningResults.map((item) => [item.url, item])).values()
            );

            // Incremental state updates
            setAllResults(uniqueResults);
            setDisplayedResults(uniqueResults.filter((link) => link.isBroken));
            setTotalUrls(uniqueResults.length);
            setCheckedUrls(uniqueResults.length);
            setOkCount(uniqueResults.filter((l) => !l.isBroken).length);
            setFailedCount(uniqueResults.filter((l) => l.isBroken).length);

            const progressPercent = Math.round(((i + 1) / totalLinks) * 100);
            setProgress(progressPercent);
          }

          setProgress(100);
          const height = document.body.scrollHeight;
          window.parent.postMessage({ type: "setHeight", height }, "*");
        } else {
          console.log("No links found for analysis.");
        }
      }
    } catch (err: any) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsChecking(false);
    }
  };

  const handleFullReport = () => {
    setShowFullReport(true);
    setDisplayedResults([...allResults]);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };
  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [displayedResults]);

  const renderStatus = () => {
    if (!isLoading && allResults.length > 0) {
      return (
        <div className="status">
          <p>
            {progress}% scanned - {checkedUrls}/{totalUrls} URLs checked,{" "}
            {okCount} OK, {failedCount} failed
          </p>
          <p>Generated: {new Date().toLocaleString()}</p>
          {!showFullReport && (
            <Button
              variant="outline-primary"
              onClick={handleFullReport}
              className="fullReportButton"
            >
              Full report
            </Button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Box  className={`${!isInIframe ? 'py-5 defaultText' : 'py-1 interDisplay'}`}>
        {!isInIframe && (
          <ToolsHeader
            title="Site Checker: Free Broken Link Tool"
            description="Quickly identify and fix broken links on your website to boost
                SEO and user experience."
          />
        )}
        <Card className="container">
          <Card.Body>
            {/* <h2 className={styles.title}>Site Checker: Free Broken Link Tool</h2> */}
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="url" className="mb-3">
                <Form.Control
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  disabled={isLoading}
                  className="input"
                  placeholder="Enter website URL (e.g., https://example.com)"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  inline
                  label="Check single webpage"
                  name="checkMode"
                  type="radio"
                  id="check-single"
                  checked={checkMode === "single"}
                  onChange={() => setCheckMode("single")}
                />
                <Form.Check
                  inline
                  label="Check whole website"
                  name="checkMode"
                  type="radio"
                  id="check-whole"
                  checked={checkMode === "whole"}
                  onChange={() => setCheckMode("whole")}
                />
              </Form.Group>

              <Button
                variant="warning"
                type="submit"
                disabled={isLoading || !url}
                className="submitButton"
                style={{ backgroundColor: "#8cc540", color: "#fff" }}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Checking...
                  </>
                ) : (
                  "Check"
                )}
              </Button>
            </Form>

            {isLoading && (
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                className="mt-3"
              />
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            {renderStatus()}

            {displayedResults.length > 0 && (
              <div style={{ width: "100%", overflow: "auto" }}>
                <Table striped bordered hover className="table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>URL</th>
                      <th>Anchor Text</th>
                      <th>Linked From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map((result, index) => (
                      <tr
                        key={index}
                        className={
                          result.isBroken
                            ? "broken"
                            : result.status === "Checking"
                            ? "checking"
                            : ""
                        }
                      >
                        <td>{result.status}</td>
                        <td>
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {result.url}
                          </a>
                        </td>
                        <td>{result.anchorText}</td>
                        <td>{result.linkedFrom}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Box>
    </>
  );
};

export default BrokenLinkChecker;
